import { useApolloClient, useQuery } from '@apollo/client';
import { MedplumClient } from '@medplum/core';
import format from 'date-fns/format';
import eruda from 'eruda';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { redirectToAuth } from 'supertokens-auth-react';
import Session from 'supertokens-auth-react/recipe/session';
import { waitFor } from 'xstate';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Caption,
  DropdownMenu,
  DropdownMenuAnchorItem,
  DropdownMenuContent,
  DropdownMenuCustomItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuLinkItem,
  DropdownMenuSelectableItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Icon,
  SidebarMenuButton,
  Stack,
  TenantCard,
  Text,
  useSidebar,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { clearSkippedInterstitials } from '@eluve/interstitials';
import {
  useIsPostHogFeatureFlagEnabled,
  usePostHog,
} from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useImpersonation,
  useImpersonationTimeRemaining,
  useIsTenantAdminFromSession,
  useSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { SystemStatusDialog } from '@eluve/site-banners';
import {
  useIsFeatureFlagEnabled,
  useTenantLogo,
  useTenantName,
  useUserName,
} from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';

import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../$tenantId/appointments/machines/transcription/hooks/useTranscriptionMachine';
import { appConfig } from '../../../../config';
import { GetTenantsList } from '../operations';

const DEVELOPER_CLICK_THRESHOLD = 5;
const DEVELOPER_CLICK_TIMEOUT_MS = 2000;
const DROPDOWN_OFFSET_AND_PADDING = 8;
const TENANT_LIST_LIMIT = 10;
/**
 * Upper bound on how long to wait for the transcription machine to finalize a
 * recording (flush the recorder + schedule the upload) after STOP, before
 * navigating to the new tenant. Comfortably above the machine's own
 * finalize-audio timeout (~1.5s) so the normal path resolves by reaching Idle;
 * this only guards against a wedged finalize trapping the user [ENG-4407].
 */
const END_RECORDING_FINALIZE_TIMEOUT_MS = 5000;
/**
 * Grace window after the machine reaches Idle before navigating. `stopRecording`
 * schedules the final upload via `scheduleFileUpload`, which hands the file to
 * the periodic sync machine on a short deferred timer (~50ms, see
 * `TranscriptionMachineProvider`). Navigating immediately would unmount that
 * sync machine before the handoff fires, leaving the final upload to be picked
 * up only later by orphan recovery rather than queued promptly. Comfortably
 * exceeds that defer [ENG-4407].
 */
const UPLOAD_HANDOFF_GRACE_MS = 250;

export const UserDropdownMenu = () => {
  const { t } = useTranslation();
  const [clickCount, setClickCount] = useState(0);

  const canManageLocalFiles = useIsFeatureFlagEnabled('LOCAL_FILES_MANAGEMENT');
  const isReportingEnabled = useIsFeatureFlagEnabled('REPORTING_DASHBOARD');
  const showEarlyAccessPanel = useIsPostHogFeatureFlagEnabled(
    'SHOW_EARLY_ACCESS_PANEL',
  );
  const client = useApolloClient();
  const isTenantAdmin = useIsTenantAdminFromSession();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const tenantId = useAssignedTenantIdFromParams();
  const tenantName = useTenantName(tenantId);
  const userId = useUserIdFromSession();

  const { data } = useQuery(GetTenantsList);
  const { isMobile, open: isOpen } = useSidebar();
  const session = useSession();
  const {
    isEluveAdmin,
    isPromptTemplateCreator,
    isEluveEvalUser,
    isReviewer,
    isMlEngineer,
  } = session;

  const {
    isImpersonating,
    isLoading: isImpersonationLoading,
    exitImpersonation,
    expiresAt: impersonationExpiresAt,
    impersonationOriginUrl,
  } = useImpersonation();

  const { tenantLogoUrl } = useTenantLogo(tenantId);

  const {
    isDialogOpen: systemStatusDialogOpen,
    toggleDialog: toggleSystemStatusDialog,
  } = useDialog();

  const date = format(Date.now(), 'yyyy-MM-dd');
  const isCollapsed = !isOpen && !isMobile;
  const tenants = (data?.assignedTenantUsers ?? []).filter(
    (tenant) => tenant.tenantId != null,
  );

  let timeout: NodeJS.Timeout;
  const handleDeveloperClick = () => {
    setClickCount((count) => count + 1);
    clearTimeout(timeout);

    if (clickCount > DEVELOPER_CLICK_THRESHOLD) {
      setClickCount(0);
      eruda.init();
    } else {
      timeout = setTimeout(() => setClickCount(0), DEVELOPER_CLICK_TIMEOUT_MS);
    }
  };

  const handleLogoutClick = async () => {
    clearSkippedInterstitials();
    posthog?.reset();
    // Revoke the Medplum session so the SDK clears its localStorage entries
    // (activeLogin + tokens). Without this, the next user to sign in briefly
    // hydrates MedplumClient with the previous user's state before the
    // token exchange overwrites it.
    const medplumBaseUrl = appConfig.VITE_MEDPLUM_BASE_URL;
    if (medplumBaseUrl) {
      try {
        await new MedplumClient({ baseUrl: medplumBaseUrl }).signOut();
      } catch {
        // ignore — nothing to sign out of, or network error
      }
    }
    await Session.signOut();
    redirectToAuth();
  };

  // A dropdown switch routes through `/organizations/switch/<id>`, which
  // remounts the tenant subtree and silently tears down any active recording.
  // Warn the user first so they don't lose an in-progress recording [ENG-4407].
  const isRecording = useTranscriptionMachineSelector((state) =>
    state.matches({ InSession: 'Recording' }),
  );
  const transcriptionActor = useTranscriptionMachineActor();
  const [pendingTenantId, setPendingTenantId] = useState<string | null>(null);
  const [isEndingRecording, setIsEndingRecording] = useState(false);

  const performTenantSwitch = (newTenantId: string) => {
    client.resetStore();
    navigate(`/organizations/switch/${newTenantId}`);
  };

  const handleTenantClick = (newTenantId: string) => {
    if (isRecording) {
      setPendingTenantId(newTenantId);
      return;
    }
    performTenantSwitch(newTenantId);
  };

  const confirmEndRecordingAndSwitch = async () => {
    if (!pendingTenantId || isEndingRecording) {
      return;
    }
    const targetTenantId = pendingTenantId;
    setIsEndingRecording(true);

    // Send STOP and wait for the machine to actually finish finalizing (flush
    // the recorder's final audio, then schedule the upload) before navigating.
    // `/organizations/switch` remounts the tenant subtree and tears down the
    // transcription machine + task processor, so navigating before finalization
    // completes would truncate the recording and drop its final upload. Bounded
    // by a timeout so a wedged finalize can't trap the user on this tenant.
    transcriptionActor.send({ type: 'STOP' });
    try {
      await waitFor(transcriptionActor, (state) => state.matches('Idle'), {
        timeout: END_RECORDING_FINALIZE_TIMEOUT_MS,
      });
      // Let the deferred upload handoff fire before the navigation below
      // unmounts the periodic sync machine, so the final file is queued
      // immediately rather than only later via orphan recovery.
      await new Promise((resolve) =>
        setTimeout(resolve, UPLOAD_HANDOFF_GRACE_MS),
      );
    } catch {
      // Finalize did not complete within the timeout; navigate anyway rather
      // than leaving the user stuck. The upload is durable via local storage +
      // periodic sync and can resume after the switch.
    }

    setPendingTenantId(null);
    setIsEndingRecording(false);
    performTenantSwitch(targetTenantId);
  };

  const userDisplayName = useUserName(userId, '');

  // Get auto-updating time remaining (updates every minute, long format)
  const timeRemaining = useImpersonationTimeRemaining(
    impersonationExpiresAt,
    'long',
  );

  return (
    <>
      <DropdownMenu modal={!isMobile}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            className="relative mt-auto mr-auto group-data-[collapsible=icon]:pl-1!"
            size="lg"
          >
            <TenantCard
              icon={{ name: 'ChevronsUpDown' }}
              showBadge={isImpersonating}
              tenantLogoUrl={tenantLogoUrl}
              tenantName={tenantName}
              userDisplayName={userDisplayName}
            />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          alignOffset={isCollapsed ? 0 : DROPDOWN_OFFSET_AND_PADDING}
          className="min-w-[200px]"
          onCloseAutoFocus={(event) => event.preventDefault()}
          sideOffset={isCollapsed ? DROPDOWN_OFFSET_AND_PADDING : 0}
        >
          {isImpersonating && (
            <>
              <DropdownMenuCustomItem>
                <Stack
                  align="center"
                  className="pointer-events-auto rounded-md border border-border-warning-subtle bg-bg-warning-subtle p-2 text-text-warning-subtle [&>*:last-child]:mt-2"
                  gap={1}
                >
                  <Icon name="AlertTriangle" />
                  <Text color="inherit" size="xs">
                    {t('userMenu.impersonatingUser')}
                  </Text>
                  {timeRemaining && (
                    <Caption className="opacity-60" color="inherit" size="3xs">
                      {timeRemaining}
                    </Caption>
                  )}
                  <Button
                    size="sm"
                    loading={isImpersonationLoading}
                    onClick={() => exitImpersonation(impersonationOriginUrl)}
                    fullWidth
                  >
                    {t('userMenu.exitImpersonation')}
                  </Button>
                </Stack>
              </DropdownMenuCustomItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuCustomItem>
            <TenantCard
              tenantName={tenantName}
              userDisplayName={userDisplayName}
              tenantLogoUrl={tenantLogoUrl}
            />
          </DropdownMenuCustomItem>
          {/* Switching organization while impersonating navigates to a tenant
            switcher that runs under the impersonated user's session — it
            either lands on a tenant they belong to (silently dropping the
            impersonation context the admin chose) or fails outright. Hide it
            for the duration of the impersonation; admin can Exit and switch
            from their own session. [ENG-4309] */}
          {!isImpersonating &&
            tenants.length > 1 &&
            tenants.length <= TENANT_LIST_LIMIT && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  {t('userMenu.switchOrganization')}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent
                  collisionPadding={DROPDOWN_OFFSET_AND_PADDING}
                >
                  {tenants.map((tenant) => (
                    <DropdownMenuSelectableItem
                      currentValue={tenantId}
                      key={tenant.tenantId}
                      onClick={handleTenantClick}
                      text={tenant.name ?? t('userMenu.unnamedOrganization')}
                      value={tenant.tenantId as string}
                    />
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
          {!isImpersonating &&
            tenants.length > 1 &&
            tenants.length > TENANT_LIST_LIMIT && (
              <DropdownMenuLinkItem
                text={t('userMenu.switchOrganization')}
                to={`/organizations`}
              />
            )}
          {isTenantAdmin && (
            <DropdownMenuLinkItem
              text={t('userMenu.organizationSettings')}
              to={`/organizations/${tenantId}/settings/organization`}
            />
          )}
          {(canManageLocalFiles || isPromptTemplateCreator) && (
            <DropdownMenuSeparator />
          )}
          {(canManageLocalFiles || isPromptTemplateCreator) && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Eluve Tools</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {isPromptTemplateCreator && (
                  <DropdownMenuLinkItem
                    text="Create Prompt Template"
                    to="/prompts"
                  />
                )}
                {isPromptTemplateCreator && (
                  <DropdownMenuLinkItem
                    text="Output Templates"
                    to={`/organizations/${tenantId}/settings/templates`}
                  />
                )}
                {canManageLocalFiles && (
                  <DropdownMenuLinkItem
                    text="Manage Device Files"
                    to={`/organizations/${tenantId}/files`}
                  />
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
          {(isEluveAdmin || isEluveEvalUser || isReviewer || isMlEngineer) && (
            <DropdownMenuSeparator />
          )}
          {isEluveAdmin && (
            <DropdownMenuLinkItem text="Admin Dashboard" to="/admin" />
          )}
          {isEluveEvalUser && (
            <DropdownMenuLinkItem
              text="Eval Dashboard"
              to="/eval/source-artifact-review"
            />
          )}
          {(isReviewer || isMlEngineer) && (
            <DropdownMenuLinkItem text="Review Dashboard" to="/review" />
          )}
          {isEluveAdmin && isReportingEnabled && (
            <DropdownMenuLinkItem
              text="Reporting Dashboard"
              to={`/organizations/${tenantId}/reporting`}
            />
          )}
          {showEarlyAccessPanel && (
            <DropdownMenuItem data-attr="early-access-button">
              {t('userMenu.betaFeatures')}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={toggleSystemStatusDialog}>
            {t('userMenu.systemStatus')}
          </DropdownMenuItem>
          <DropdownMenuAnchorItem
            href={`mailto:feedback@eluve.com?subject=Eluve Feedback - ${date}`}
            text={t('userMenu.contactUs')}
          />
          <DropdownMenuItem onClick={handleLogoutClick}>
            {t('userMenu.logOut')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel onClick={handleDeveloperClick}>
            {appConfig.VITE_APP_VERSION}
          </DropdownMenuLabel>
        </DropdownMenuContent>
        <SystemStatusDialog
          open={systemStatusDialogOpen}
          toggleOpen={toggleSystemStatusDialog}
        />
      </DropdownMenu>
      <AlertDialog
        open={pendingTenantId !== null}
        onOpenChange={(open) => {
          // Don't allow dismissing the dialog while we're finalizing — the
          // recording must finish flushing before we navigate away.
          if (!open && !isEndingRecording) {
            setPendingTenantId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('userMenu.endRecordingToSwitchTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('userMenu.endRecordingToSwitchDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isEndingRecording}
              onClick={() => setPendingTenantId(null)}
            >
              {t('userMenu.keepRecording')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isEndingRecording}
              onClick={(e) => {
                // Keep the dialog open while finalizing; navigation (after the
                // recorder flush completes) unmounts it.
                e.preventDefault();
                confirmEndRecordingAndSwitch();
              }}
            >
              {isEndingRecording
                ? t('userMenu.ending')
                : t('userMenu.endAndSwitch')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
